/**
 * ActionErrorNotice — compact per-action error notice for failed actions.
 *
 * Renders a user-friendly primary message and optionally surfaces the raw
 * technical detail behind an opt-in "Technical details" disclosure, keeping
 * system internals secondary and never as the primary user-facing text
 * (tone guide § 'Never Expose System Internals').
 */

import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import type { ProposedAction } from '@/lib/proposals/core/types';
import {
  getActionErrorDetail,
  hasFailedExecutionResult,
} from '@/lib/proposals/utils/actionStatus';

interface ActionErrorNoticeProps {
  action: ProposedAction;
}

const PRIMARY_MESSAGE =
  "This action couldn't be completed. Adjust it and approve again to retry.";

export function ActionErrorNotice({ action }: ActionErrorNoticeProps) {
  const [showDetail, setShowDetail] = useState(false);
  const toggleDetail = useCallback(() => setShowDetail(prev => !prev), []);

  // Suppress the notice for deselected actions, mirroring the status icon:
  // `getActionStatus` returns `pending` (no icon) for `is_selected === false`
  // regardless of any retained `execution_result`. Without this guard, a
  // deselected action that still carries a stale failed result would show the
  // failure notice while the status UI shows nothing — telling the user an
  // excluded action failed and leaving a misleading retry message visible.
  if (action.is_selected === false) {
    return null;
  }

  // Gate on the raw execution result recording an actual failure
  // (`error`/`failure`/`partial_success`), NOT on the presence of a technical
  // detail string. A failed action with no detail text still needs the primary
  // user-facing message — the raw detail only drives the opt-in disclosure
  // below. We deliberately do NOT gate on `getActionStatus(action) === 'error'`:
  // its fallback also maps `skipped` (e.g. a dependency was deselected) and
  // transient `in_flight` statuses to `'error'`, which would mislabel an
  // expected skip as a failure.
  if (!hasFailedExecutionResult(action)) {
    return null;
  }

  const detail = getActionErrorDetail(action);

  return (
    <div className='mb-3 rounded-lg border border-[var(--color-border-error_subtle,var(--color-border-secondary))] bg-[var(--color-bg-primary)] p-3'>
      <p className='text-sm text-[var(--color-text-error-primary)]'>
        {PRIMARY_MESSAGE}
      </p>
      {detail && (
        <>
          <Button
            color='tertiary'
            size='sm'
            onClick={toggleDetail}
            className='mt-1 h-auto p-0 text-xs font-medium underline-offset-2 hover:underline'
          >
            {showDetail ? 'Hide technical details' : 'Technical details'}
          </Button>
          {showDetail && (
            <pre className='mt-2 overflow-x-auto whitespace-pre-wrap break-all rounded bg-[var(--color-bg-secondary)] p-2 font-mono text-xs text-[var(--color-text-secondary)]'>
              {detail}
            </pre>
          )}
        </>
      )}
    </div>
  );
}
